'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { Search } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { MorphExpandIcon } from '@/components/ui/morph-expand-icon'
import { Input } from '@/components/ui/input'
import { footerNav, mainNav, orderNavItems, type NavItem } from '@/config/portal-nav'
import type { MainNavKey, UtilityNavKey } from '@/config/portal-navigation'
import { Logo, LogoMark } from './logo'

/**
 * Full wordmark at `md` and up; swaps to the crest-only `LogoMark` below
 * `md` — the breakpoint where the sidebar collapses behind the hamburger menu.
 */
export function PortalLogo({ className }: { className?: string }) {
  return (
    <Link
      href="/home"
      className={cn('flex items-center px-2 py-1', className)}
      aria-label="Member Portal home"
    >
      {/* Color is inherited from the Link's text color (currentColor). */}
      <Logo className="hidden h-auto w-[180px] md:block" />
      <LogoMark className="h-7 w-auto md:hidden" />
    </Link>
  )
}

/** Sidebar search field (moved here from the header to match the portal design). */
export function SidebarSearch() {
  return (
    <div className="relative px-2">
      <Search className="text-muted-foreground absolute top-1/2 left-5 size-4 -translate-y-1/2" />
      <Input
        type="search"
        placeholder="Search..."
        aria-label="Search"
        className="bg-card rounded-full pl-9"
      />
    </div>
  )
}

// Active = accent wash (selection). Hover on inactive = quiet muted wash so
// the current page stays visually distinct — Apple sidebar selection pattern.
const navLinkClass = (active: boolean) =>
  cn(
    'group focus-visible:ring-sidebar-ring focus-visible:ring-offset-background flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    active
      ? 'bg-sidebar-accent text-sidebar-primary font-semibold'
      : 'text-sidebar-foreground hover:bg-interaction-neutral focus-visible:bg-interaction-neutral',
  )

const navIconClass = (active: boolean) =>
  cn(
    'size-5 shrink-0 transition-colors',
    active
      ? 'text-sidebar-primary'
      : 'text-sidebar-foreground/70 group-hover:text-sidebar-foreground group-focus-visible:text-sidebar-foreground',
  )

function NavLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const pathname = usePathname()
  const active = pathname === item.href || pathname.startsWith(`${item.href}/`)
  const Icon = item.icon

  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={navLinkClass(active)}
    >
      <Icon className={navIconClass(active)} />
      <span>{item.label}</span>
    </Link>
  )
}

/** Nav item with an expandable sub-nav (Collapsible), e.g. Partner Programs. */
function NavGroup({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const pathname = usePathname()
  const Icon = item.icon
  const childActive = item.children?.some(
    (c) => pathname === c.href || pathname.startsWith(`${c.href}/`),
  )
  const active = pathname === item.href || childActive
  // Open state is owned here (not `defaultOpen`) so the chevron can morph with it.
  const [open, setOpen] = useState(!!childActive)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className={cn(navLinkClass(!!active), 'w-full')}>
        <Icon className={navIconClass(!!active)} />
        <span className="flex-1 text-left">{item.label}</span>
        <MorphExpandIcon expanded={open} className="ml-auto" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 flex flex-col gap-1 pl-8">
        {item.children?.map((child) => {
          const cActive = pathname === child.href || pathname.startsWith(`${child.href}/`)
          return (
            <Link
              key={child.href}
              href={child.href}
              onClick={onNavigate}
              aria-current={cActive ? 'page' : undefined}
              className={cn(
                'focus-visible:ring-sidebar-ring focus-visible:ring-offset-background rounded-md px-3 py-1.5 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                cActive
                  ? 'bg-sidebar-accent text-sidebar-primary font-semibold'
                  : 'text-sidebar-foreground/80 hover:bg-interaction-neutral focus-visible:bg-interaction-neutral',
              )}
            >
              {child.label}
            </Link>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Nav list, reused by the desktop sidebar and the mobile sheet. */
type SidebarNavProps = {
  mainOrder?: readonly MainNavKey[]
  utilityOrder?: readonly UtilityNavKey[]
  onNavigate?: () => void
}

export function SidebarNav({ mainOrder, utilityOrder, onNavigate }: SidebarNavProps) {
  const orderedMainNav = orderNavItems(mainNav, mainOrder)
  const orderedFooterNav = orderNavItems(footerNav, utilityOrder)

  return (
    <div className="flex flex-col">
      <nav className="flex flex-col gap-1">
        {orderedMainNav.map((item) =>
          item.children ? (
            <NavGroup key={item.href} item={item} onNavigate={onNavigate} />
          ) : (
            <NavLink key={item.href} item={item} onNavigate={onNavigate} />
          ),
        )}
      </nav>
      <nav className="border-sidebar-border mt-4 flex flex-col gap-1 border-t pt-4">
        {orderedFooterNav.map((item) => (
          <NavLink key={item.href} item={item} onNavigate={onNavigate} />
        ))}
      </nav>
    </div>
  )
}

/** Persistent desktop sidebar (hidden on mobile — see PortalHeader's sheet). */
export function PortalSidebar({ mainOrder, utilityOrder }: Omit<SidebarNavProps, 'onNavigate'>) {
  return (
    <aside className="border-sidebar-border bg-sidebar text-sidebar-foreground sticky top-0 hidden h-screen w-72 shrink-0 flex-col gap-4 overflow-y-auto border-r p-4 md:flex">
      <PortalLogo className="text-brand-maroon mb-4" />
      <SidebarSearch />
      <SidebarNav mainOrder={mainOrder} utilityOrder={utilityOrder} />
    </aside>
  )
}
